import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CountyResearchRecord } from "./types";
import { formatCurrency } from "@/components/investor/utils";
import { format } from "date-fns";
import { ExternalLink, FileText, AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface RecordDetailModalProps {
  record: CountyResearchRecord;
  open: boolean;
  onClose: () => void;
  onUpdate?: () => void;
}

export function RecordDetailModal({ record, open, onClose, onUpdate }: RecordDetailModalProps) {
  const navigate = useNavigate();
  const [manualAddress, setManualAddress] = useState(record.grantee_address || '');
  const [isSaving, setIsSaving] = useState(false);

  const handleSaveAddress = async () => {
    if (!manualAddress.trim()) {
      toast.error("Please enter an address");
      return;
    }

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('county_research')
        .update({
          grantee_address: manualAddress,
          address_needs_review: false,
          address_extraction_method: 'manual',
          address_confidence_score: 100,
          updated_at: new Date().toISOString()
        })
        .eq('id', record.id);

      if (error) throw error;

      toast.success("Address saved successfully");
      onUpdate?.();
      onClose();
    } catch (error) {
      console.error("Error saving address:", error);
      toast.error("Failed to save address");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateDeal = () => {
    toast.success("Creating deal from research record...");
    navigate('/admin-portal/deals/new', { 
      state: { 
        prefill: {
          deal_title: `${record.property_address || 'Property'} - ${record.grantee_name}`,
          seller_name: record.grantee_name,
          seller_address: record.grantee_address,
          buyer_name: record.grantor_name,
          property_address: record.property_address,
          sales_price: record.loan_amount,
          interest_rate: record.interest_rate,
          payment_amount: record.monthly_payment,
          original_term_months: record.original_term_months,
          has_balloon_payment: (record.balloon_amount || 0) > 0,
          balloon_amount: record.balloon_amount,
          balloon_due_date: record.balloon_due_date,
          date_of_sale: record.filing_date,
          status: 'draft',
          internal_notes: `Found via county research. Doc #${record.document_number}. Confidence: ${record.confidence_score}%`
        }
      }
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Document #{record.document_number}</span>
            <div className="flex gap-2">
              {record.is_active ? (
                <Badge variant="default">Active</Badge>
              ) : (
                <Badge variant="secondary">Inactive</Badge>
              )}
              {record.is_potential_owner_finance && (
                <Badge variant="default">Owner Finance</Badge>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>

        {!record.is_active && (
          <Alert variant="default">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Lien Released</AlertTitle>
            <AlertDescription>
              Released on {record.release_date && format(new Date(record.release_date), 'MM/dd/yyyy')} 
              {record.release_document_number && ` via document ${record.release_document_number}`}
            </AlertDescription>
          </Alert>
        )}

        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="parties">Parties</TabsTrigger>
            <TabsTrigger value="financial">Financial</TabsTrigger>
            <TabsTrigger value="document">Document</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Key Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Filing Date</p>
                    <p className="font-medium">{record.filing_date ? format(new Date(record.filing_date + 'T12:00:00'), 'MMMM dd, yyyy') : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Document Type</p>
                    <p className="font-medium">{record.document_type || 'Deed of Trust'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Property Address</p>
                    <p className="font-medium">{record.property_address || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Confidence Score</p>
                    <Badge variant={(record.confidence_score || 0) > 80 ? 'default' : 'secondary'}>
                      {record.confidence_score || 0}%
                    </Badge>
                  </div>
                </div>

                {record.detection_reasons && record.detection_reasons.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium mb-2">Detection Reasons:</p>
                    <ul className="list-disc list-inside space-y-1">
                      {record.detection_reasons.map((reason, idx) => (
                        <li key={idx} className="text-sm text-muted-foreground">{reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="parties" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Seller/Lender (Grantee)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="font-semibold text-lg">{record.grantee_name}</p>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-muted-foreground">Mailing Address</p>
                      {record.address_extraction_method && (
                        <Badge variant="outline" className="text-xs">
                          {record.address_extraction_method}
                        </Badge>
                      )}
                    </div>

                    {record.address_needs_review ? (
                      <div className="space-y-2">
                        <Alert>
                          <AlertDescription>
                            Address needs manual review. Please enter seller's mailing address below.
                          </AlertDescription>
                        </Alert>
                        <Textarea
                          placeholder="Enter seller's mailing address"
                          value={manualAddress}
                          onChange={(e) => setManualAddress(e.target.value)}
                          rows={3}
                        />
                        <Button 
                          size="sm" 
                          onClick={handleSaveAddress}
                          disabled={isSaving}
                        >
                          {isSaving ? "Saving..." : "Save Manual Address"}
                        </Button>
                      </div>
                    ) : record.grantee_address ? (
                      <div>
                        <p className="whitespace-pre-wrap">{record.grantee_address}</p>
                        {record.address_confidence_score && (
                          <Badge variant="outline" className="mt-2">
                            Confidence: {record.address_confidence_score}%
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <p className="text-muted-foreground italic">Not extracted</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Seller (Grantor)</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-medium">{record.grantor_name}</p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="financial" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Financial Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Loan Amount</p>
                    <p className="text-xl font-semibold">
                      {record.loan_amount ? formatCurrency(record.loan_amount) : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Interest Rate</p>
                    <p className="text-xl font-semibold">
                      {record.interest_rate ? `${(record.interest_rate * 100).toFixed(2)}%` : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Monthly Payment</p>
                    <p className="text-xl font-semibold">
                      {record.monthly_payment ? formatCurrency(record.monthly_payment) : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Term</p>
                    <p className="text-xl font-semibold">
                      {record.term_months ? `${record.term_months} months` : 'N/A'}
                    </p>
                  </div>
                  {record.balloon_amount && record.balloon_amount > 0 && (
                    <>
                      <div>
                        <p className="text-sm text-muted-foreground">Balloon Payment</p>
                        <p className="text-xl font-semibold">{formatCurrency(record.balloon_amount)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Balloon Due Date</p>
                        <p className="text-xl font-semibold">
                          {record.balloon_due_date && format(new Date(record.balloon_due_date), 'MM/dd/yyyy')}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="document" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Document Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  {record.pdf_url && (
                    <Button
                      variant="outline"
                      onClick={() => window.open(record.pdf_url, '_blank')}
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      View Original on Tyler Host
                    </Button>
                  )}
                  {record.pdf_downloaded && (
                    <Button variant="outline">
                      <FileText className="mr-2 h-4 w-4" />
                      Re-extract Address (OCR)
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Pages</p>
                    <p className="font-medium">{record.number_of_pages || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">PDF Downloaded</p>
                    <p className="font-medium">{record.pdf_downloaded ? 'Yes' : 'No'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">OCR Attempted</p>
                    <p className="font-medium">{record.ocr_attempted ? 'Yes' : 'No'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {record.notes && (
              <Card>
                <CardHeader>
                  <CardTitle>Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="whitespace-pre-wrap">{record.notes}</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={handleCreateDeal}>
            Create Deal from This Record
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
